/**
 * The orca routes that must stay reachable without a session: the login door,
 * the self-service sign-up door, and everything under either of them (Clerk's
 * path-routed `<SignIn>` / `<SignUp>` steps such as `/orca/login/factor-one`,
 * `/orca/sign-up/verify-email-address` and both `sso-callback`s), plus the two
 * legal documents (`/orca/privacy`, `/orca/terms`) and the signed-in
 * customer pages that run their own `requireAuth` guard (`/orca/collect`,
 * `/orca/connect`, `/orca/artifacts`, `/orca/loops`). Everything else under
 * `/orca` is admin-only and is guarded by the `/orca` layout route.
 *
 * Pathnames are the INTERNAL (prefix-rewritten) form the router matches on.
 *
 * @since 1.0.0
 */
export const ORCA_LOGIN_PATH = "/orca/login";

/**
 * The orca self-service sign-up door (`orcaloops.ai/sign-up` publicly).
 *
 * @since 1.0.0
 */
export const ORCA_SIGN_UP_PATH = "/orca/sign-up";

/**
 * The public Orcaloops Privacy Policy (`orcaloops.ai/privacy`). Must stay
 * reachable with no session — Google's OAuth "Publish app" review fetches it
 * anonymously on the authorized domain.
 *
 * @since 1.0.0
 */
export const ORCA_PRIVACY_PATH = "/orca/privacy";

/**
 * The public Orcaloops Terms of Service (`orcaloops.ai/terms`). Public for the
 * same reason as {@link ORCA_PRIVACY_PATH}.
 *
 * @since 1.0.0
 */
export const ORCA_TERMS_PATH = "/orca/terms";

/**
 * The person's credential-collection page (`orcaloops.ai/collect/<token>`).
 *
 * "Public" here means ESCAPED FROM THE ADMIN GATE, not unauthenticated: the
 * route's own `beforeLoad` calls `requireAuth`, and the collect RPCs behind it
 * compare `CurrentActor.person.id` to the link's `person_id`. A person handing
 * over their own GitHub token or Claude subscription is almost never an
 * `admin`, so `requireAdmin` would bounce exactly the human the link was sent
 * to — while dropping the guard entirely would serve the form to anybody
 * holding a forwarded link.
 *
 * @since 1.0.0
 */
export const ORCA_COLLECT_PATH = "/orca/collect";

/**
 * The OAuth approve page (`orcaloops.ai/connect?<OAuth query>`), where a
 * signed-in person lets Claude Code act as them in one of their workspaces.
 *
 * Escaped from the admin gate for exactly the reason {@link ORCA_COLLECT_PATH}
 * is: `connect.tsx` re-applies `requireAuth` itself, and the `oauthConsent.*`
 * RPCs behind it take the subject from the Clerk session and accept only a
 * workspace the person is a member of. A person connecting Claude Code is
 * almost never an `admin`, so `requireAdmin` would bounce the only audience
 * this page has.
 *
 * @since 1.0.0
 */
export const ORCA_CONNECT_PATH = "/orca/connect";

/**
 * The customer's own run-artifacts page (`orcaloops.ai/artifacts/<identifier>`).
 *
 * Escaped from the admin gate for exactly the reason {@link ORCA_COLLECT_PATH}
 * is: `artifacts.$issueIdentifier.tsx` re-applies `requireAuth` itself, and the
 * `orcaloops.archive.*` RPCs behind it resolve the owning customer from
 * `CurrentActor` — never from the URL, which carries no customer id. The person
 * reading their own build's transcript is not an `admin`, so `requireAdmin`
 * would bounce the only audience this page has.
 *
 * @since 1.0.0
 */
export const ORCA_ARTIFACTS_PATH = "/orca/artifacts";

/**
 * The member's own read-only loop canvas (`orcaloops.ai/loops/<id>/canvas`).
 *
 * Escaped from the admin gate for exactly the reason {@link ORCA_ARTIFACTS_PATH}
 * is: `loops/$loopId/canvas.tsx` re-applies `requireAuth` itself, and the
 * `LoopDefinitionRpcs` behind it resolve the owning customer from the actor —
 * never from the URL, which carries no customer id. A person reading their own
 * loop's document is almost never an `admin`, so `requireAdmin` would bounce the
 * only audience this page has.
 *
 * @since 1.0.0
 */
export const ORCA_LOOPS_PATH = "/orca/loops";

/**
 * The member's own Inbox (`orcaloops.ai/inbox`) — every run waiting on them,
 * answered inline.
 *
 * Escaped from the admin gate for exactly the reason {@link ORCA_LOOPS_PATH}
 * is: `inbox/index.tsx` re-applies `requireAuth` itself, and the
 * `LoopHumanWaitRpcs` behind it resolve the owning customer from the actor —
 * never from the URL, which carries no customer id. A person answering their
 * own runs is almost never an `admin`, so `requireAdmin` would bounce the only
 * audience this page has.
 *
 * @since 1.0.0
 */
export const ORCA_INBOX_PATH = "/orca/inbox";

/**
 * Orca v3 (`orcaloops.ai/v3/…`): the member's saved loops and runs.
 *
 * Escaped from the admin gate for exactly the reason {@link ORCA_LOOPS_PATH} is: every
 * `v3/**` route re-applies `requireAuth` itself, and the `V3LoopRpcs` / `V3RunRpcs`
 * behind them resolve the owning customer from the actor, never from the URL.
 *
 * @since 1.0.0
 */
export const ORCA_V3_PATH = "/orca/v3";

/**
 * Every public root under `/orca`: the two doors, the two legal documents, the
 * credential-collection page, the OAuth approve page, the customer artifacts
 * page, the member loop canvas, the member Inbox and the v3 pages. A pathname is public when
 * it IS one of these or lives beneath one — never on a mere prefix match, so
 * `/orca/loginx`, `/orca/sign-upx`, `/orca/privacyx`, `/orca/termsx`,
 * `/orca/collectx`, `/orca/connectx`, `/orca/artifactsx`, `/orca/loopsx` and
 * `/orca/inboxx` and `/orca/v3x` stay guarded.
 *
 * @since 1.0.0
 */
const ORCA_PUBLIC_ROOTS = [
  ORCA_LOGIN_PATH,
  ORCA_SIGN_UP_PATH,
  ORCA_PRIVACY_PATH,
  ORCA_TERMS_PATH,
  ORCA_COLLECT_PATH,
  ORCA_CONNECT_PATH,
  ORCA_ARTIFACTS_PATH,
  ORCA_LOOPS_PATH,
  ORCA_INBOX_PATH,
  ORCA_V3_PATH,
] as const;

/**
 * Whether an internal orca pathname is public (a door or a Clerk step beneath
 * one).
 *
 * @since 1.0.0
 */
export const isOrcaPublicPath = (pathname: string): boolean =>
  ORCA_PUBLIC_ROOTS.some((root) => pathname === root || pathname.startsWith(`${root}/`));
